import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Merge } from "lucide-react";
import { toast } from "sonner";

const weight = (p) => p.stock * 1000 + p.sales_lines * 10 + p.movements + (p.is_active ? 0.5 : 0);
const pickKeeper = (ps) => [...ps].sort((a, b) => weight(b) - weight(a) || a.sku.localeCompare(b.sku))[0].id;
const sameBrand = (ps) => ps.some((p, i) => ps.findIndex((q) => q.brand_key === p.brand_key) !== i);

function GroupCard({ g, onMerged }) {
  const [keep, setKeep] = useState(() => pickKeeper(g.products));
  const keeper = g.products.find((p) => p.id === keep);
  const [sel, setSel] = useState(() => new Set(g.products.filter((p) => p.id !== keep && p.brand_key === keeper.brand_key).map((p) => p.id)));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const chooseKeep = (id) => {
    const k = g.products.find((p) => p.id === id);
    setKeep(id); setConfirm(false);
    setSel(new Set(g.products.filter((p) => p.id !== id && p.brand_key === k.brand_key).map((p) => p.id)));
  };
  const toggle = (id, v) => { setConfirm(false); setSel((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; }); };
  const merge = async () => {
    setBusy(true);
    try {
      await api.post("/products/merge", { keep_id: keep, merge_ids: [...sel] });
      toast.success(`Merged ${sel.size} duplicate(s) into ${keeper.name}`);
      onMerged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`dup-group-${keeper.sku}`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{g.key}</div>
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground text-left"><tr>
          <th className="py-1 w-16">Keep</th><th className="py-1 w-16">Merge</th><th className="py-1">Product</th><th className="py-1">Brand</th>
          <th className="py-1 text-right">Cost</th><th className="py-1 text-right">Stock</th><th className="py-1 text-right">Sales lines</th><th className="py-1 text-right">Movements</th></tr></thead>
        <tbody>{g.products.map((p) => {
          const isKeep = p.id === keep;
          return (
            <tr key={p.id} className={`border-t ${isKeep ? "bg-emerald-50/60" : sel.has(p.id) ? "bg-rose-50/50" : ""}`}>
              <td className="py-1.5"><input type="radio" name={`keep-${g.key}`} checked={isKeep} onChange={() => chooseKeep(p.id)} data-testid={`dup-keep-${p.sku}`} /></td>
              <td className="py-1.5">{!isKeep && <Checkbox checked={sel.has(p.id)} onCheckedChange={(v) => toggle(p.id, !!v)} data-testid={`dup-merge-${p.sku}`} />}</td>
              <td className="py-1.5">{p.name} <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>{!p.is_active && <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>}</td>
              <td className="py-1.5">{p.brand || <span className="text-muted-foreground">—</span>}
                {!isKeep && p.brand_key !== keeper.brand_key && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] text-amber-800">different brand</span>}</td>
              <td className="py-1.5 text-right font-mono">{fmtGHS(p.cost_price)}</td>
              <td className="py-1.5 text-right font-mono">{p.stock}</td><td className="py-1.5 text-right font-mono">{p.sales_lines}</td><td className="py-1.5 text-right font-mono">{p.movements}</td>
            </tr>);
        })}</tbody>
      </table>
      <div className="flex items-center gap-2">
        {!confirm ? <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => setConfirm(true)} data-testid={`dup-merge-btn-${keeper.sku}`}><Merge className="h-3.5 w-3.5 mr-1" /> Merge {sel.size || ""} into kept product</Button>
          : <>
            <Button size="sm" variant="destructive" disabled={busy} onClick={merge} data-testid={`dup-confirm-btn-${keeper.sku}`}>{busy ? "Merging…" : `Yes, merge and delete ${sel.size}`}</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
            <span className="text-xs text-muted-foreground">Stock, history and barcodes move to {keeper.name} ({keeper.sku}). This can't be undone.</span>
          </>}
      </div>
    </div>
  );
}

export const DuplicateWarning = ({ matches }) => (
  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm space-y-1.5" data-testid="duplicate-warning">
    <div className="font-medium text-amber-900">This looks like a product that already exists</div>
    <ul className="space-y-0.5 max-h-32 overflow-y-auto">{matches.map((m) => (
      <li key={m.id} className="text-amber-900" data-testid={`duplicate-match-${m.sku}`}>
        {m.name} <span className="font-mono text-xs">{m.sku}</span>
        {m.same_brand ? <span className="ml-1.5 rounded bg-rose-100 px-1 text-[10px] text-rose-700">same brand</span> : <span className="ml-1.5 text-xs text-amber-700">different brand</span>}
        {!m.is_active && <span className="ml-1 text-xs">(inactive)</span>}
      </li>))}</ul>
    <div className="text-xs text-amber-800">Same name, strength and dosage form. Cancel to use the existing product, or save anyway if this is genuinely different.</div>
  </div>
);

// Super Admin: review duplicate product sets and merge them into one product.
export function DuplicatesDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("same");
  const [q, setQ] = useState("");
  const load = () => { api.get("/products/duplicates").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { if (open) { load(); } }, [open]);
  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.groups || []).filter((g) => (tab === "same") === sameBrand(g.products) && (!s || g.products.some((p) => `${p.name} ${p.sku}`.toLowerCase().includes(s))));
  }, [data, tab, q]);
  const nSame = (data?.groups || []).filter((g) => sameBrand(g.products)).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen h-screen sm:rounded-none flex flex-col" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5" /> Duplicate products</DialogTitle>
          <DialogDescription>Products with the same name, strength and dosage form. Pick the one to keep, tick the ones to merge into it.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={setTab}><TabsList>
            <TabsTrigger value="same" data-testid="dup-tab-same">Same brand ({nSame})</TabsTrigger>
            <TabsTrigger value="diff" data-testid="dup-tab-diff">Different brands only ({(data?.groups.length || 0) - nSame})</TabsTrigger>
          </TabsList></Tabs>
          <Input className="max-w-xs h-9" placeholder="Search name or SKU" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dup-search" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {!data && <div className="text-sm text-muted-foreground">Looking for duplicates…</div>}
          {data && groups.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center" data-testid="dup-empty">No duplicate sets here</div>}
          {groups.map((g) => <GroupCard key={`${g.key}|${g.products.map((p) => p.id).join(",")}`} g={g} onMerged={() => { load(); onChanged(); }} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
